/**
 * Подписи дат для интерфейса на текущем языке (Intl + словарь lib.*).
 *
 * Своих дат приложение не считает: ISO-строки приходят из API и shared, а здесь
 * только форматирование — без библиотек и без new Date() по локальной зоне,
 * чтобы день не съезжал на устройстве с другим часовым поясом.
 */

import { daysBetweenIso, shiftIsoDate } from '@trestly/shared';
import { intlLocale, t } from '@/i18n';

/** Арифметика дат живёт в shared, здесь только реэкспорт под привычными именами. */
export { shiftIsoDate };
export { daysBetweenIso as daysBetween };

/** Полдень UTC: день не съедет ни в одном поясе, Intl форматирует в UTC. */
function utcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day, 12));
}

/** «сентябрь», «верасень», «September»: номинатив, для подписи периода («Осталось за сентябрь»). */
export function monthName(month: number): string {
  if (month < 1 || month > 12) return String(month);
  return new Intl.DateTimeFormat(intlLocale(), { month: 'long', timeZone: 'UTC' }).format(utcDate(2000, month, 1));
}

/** «12 сентября», «12 верасня», «September 12» из YYYY-MM-DD. */
export function formatIsoDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!month || !day) return iso;
  return new Intl.DateTimeFormat(intlLocale(), { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
    utcDate(year || 2000, month, day),
  );
}

/** «25.01» из YYYY-MM-DD — компактно, для границ периода в переключателе. */
export function formatDayMonth(iso: string): string {
  const [, month, day] = iso.split('-');
  if (!month || !day) return iso;
  return `${day}.${month}`;
}

/**
 * Заголовок дня в списке трат: «Сегодня», «Вчера» или «12 сентября».
 * Границы считаем по календарным строкам, а не по разнице timestamp'ов:
 * «вчера» должно оставаться вчера и в 00:01, и в 23:59.
 */
export function dayHeading(iso: string, todayIso: string): string {
  if (iso === todayIso) return t('lib.today');
  const yesterday = shiftIsoDate(todayIso, -1);
  if (iso === yesterday) return t('lib.yesterday');
  return formatIsoDate(iso);
}

/** «через 3 дня», «сегодня», «просрочен на 2 дня» — для платежей. */
export function dueLabel(dueIso: string, todayIso: string): string {
  const diff = daysBetweenIso(todayIso, dueIso);
  if (diff === 0) return t('lib.due.today');
  if (diff === 1) return t('lib.due.tomorrow');
  if (diff > 1) return t('lib.due.inDays', { n: diff });
  return t('lib.due.late', { n: -diff });
}
